import { useState, useEffect } from 'react';
import axios from 'axios';
import { Receipt, Trash, PencilSimple, ArrowLeft, Check, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

export const ExpensesDetailView = ({ onBack }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [stats, setStats] = useState({ total: 0, count: 0, byCategory: {} });

  const EXPENSE_CATEGORIES = ['General', 'Rent', 'Utilities', 'Salaries', 'Supplies', 'Transportation', 'Marketing', 'Other'];
  const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6'];

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount || 0);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/expenses/detailed`);
      setExpenses(response.data);
      const total = response.data.reduce((sum, e) => sum + e.amount, 0);
      const byCategory = response.data.reduce((acc, e) => { acc[e.category || 'General'] = (acc[e.category || 'General'] || 0) + e.amount; return acc; }, {});
      setStats({ total, count: response.data.length, byCategory });
    } catch (error) { toast.error('Failed to load expenses'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchExpenses(); }, []);

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setEditForm({ ...expense });
  };

  const handleUpdate = async () => {
    try {
      if (editingExpense.expense_id) {
        await axios.put(`${API}/expenses/${editingExpense.expense_id}`, editForm);
      } else {
        await axios.put(`${API}/expenses`, editForm, { params: { date: editingExpense.date, bill_number: editingExpense.bill_number } });
      }
      toast.success('Expense updated!');
      setEditingExpense(null);
      fetchExpenses();
    } catch (error) { toast.error('Failed to update expense'); }
  };

  const handleDelete = async (expense) => {
    if (!window.confirm(`Delete expense ${expense.bill_number || expense.expense_id}?`)) return;
    try {
      if (expense.expense_id) {
        await axios.delete(`${API}/expenses/${expense.expense_id}`);
      } else {
        await axios.delete(`${API}/expenses`, { params: { date: expense.date, bill_number: expense.bill_number } });
      }
      toast.success('Expense deleted!');
      fetchExpenses();
    } catch (error) { toast.error('Failed to delete expense'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7fa] to-[#e8ecf1] p-6" data-testid="expenses-detail-view">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={onBack} className="premium-button"><ArrowLeft size={20} className="mr-2" /> Back</Button>
          <h1 className="heading text-4xl font-bold gradient-danger">Expenses Details</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="premium-card stat-card p-6">
            <p className="text-sm text-gray-600 mb-1">Total Expenses</p>
            <h3 className="currency text-3xl font-bold gradient-danger">{formatCurrency(stats.total)}</h3>
          </Card>
          <Card className="premium-card stat-card p-6">
            <p className="text-sm text-gray-600 mb-1">Total Entries</p>
            <h3 className="text-3xl font-bold gradient-text">{stats.count}</h3>
          </Card>
          <Card className="premium-card stat-card p-6">
            <p className="text-sm text-gray-600 mb-1">Categories</p>
            <h3 className="text-3xl font-bold gradient-text">{Object.keys(stats.byCategory).length}</h3>
          </Card>
        </div>

        <Card className="premium-card p-6 mb-6">
          <h2 className="heading text-lg font-bold mb-4 text-gray-900">Expenses by Category</h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={Object.entries(stats.byCategory).map(([name, value]) => ({ name, value }))} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                {Object.keys(stats.byCategory).map((cat, i) => <Cell key={cat} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="premium-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="premium-table w-full" data-testid="expenses-table">
              <thead>
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Bill No</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Category</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Vendor</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Date</th>
                  <th className="px-4 py-4 text-right text-sm font-semibold">Amount</th>
                  <th className="px-4 py-4 text-center text-sm font-semibold">Payment</th>
                  <th className="px-4 py-4 text-center text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 ? (
                  <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-500">No expenses found</td></tr>
                ) : expenses.map((expense, i) => (
                  <tr key={expense.expense_id || i} className="hover:bg-gray-50" data-testid={`expense-row-${i}`}>
                    <td className="px-4 py-4 font-bold text-gray-900">{expense.bill_number || '-'}</td>
                    <td className="px-4 py-4"><span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">{expense.category || 'General'}</span></td>
                    <td className="px-4 py-4">
                      <p className="font-medium text-gray-900 text-sm">{expense.vendor_name || '-'}</p>
                      {expense.vendor_contact && <p className="text-xs text-gray-500">{expense.vendor_contact}</p>}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">{expense.date}</td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-bold text-red-600">{formatCurrency(expense.amount)}</span>
                      {expense.payment_type === 'partial' && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          Paid: {formatCurrency(expense.paid_amount)} | Rem: {formatCurrency(expense.remaining_balance)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${expense.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {expense.payment_status === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(expense)} data-testid={`edit-expense-${i}`}><PencilSimple size={16} /></Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(expense)} className="text-red-600 hover:bg-red-50" data-testid={`delete-expense-${i}`}><Trash size={16} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Dialog open={editingExpense !== null} onOpenChange={() => setEditingExpense(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle className="heading text-xl font-bold">Edit Expense</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-semibold">Bill Number</label><Input value={editForm.bill_number || ''} onChange={(e) => setEditForm({ ...editForm, bill_number: e.target.value })} /></div>
              <div><label className="text-sm font-semibold">Amount</label><Input type="number" step="0.01" value={editForm.amount || ''} onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div>
              <label className="text-sm font-semibold">Category</label>
              <Select value={editForm.category || 'General'} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-semibold">Vendor Name</label><Input value={editForm.vendor_name || ''} onChange={(e) => setEditForm({ ...editForm, vendor_name: e.target.value })} /></div>
              <div><label className="text-sm font-semibold">Vendor Contact</label><Input value={editForm.vendor_contact || ''} onChange={(e) => setEditForm({ ...editForm, vendor_contact: e.target.value })} /></div>
            </div>
            <div><label className="text-sm font-semibold">Remarks</label><Input value={editForm.remarks || ''} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })} /></div>
            <div>
              <label className="text-sm font-semibold">Payment Type</label>
              <Select value={editForm.payment_type || 'full'} onValueChange={(v) => {
                if (v === 'full') setEditForm({ ...editForm, payment_type: v, paid_amount: editForm.amount, remaining_balance: 0, payment_status: 'paid' });
                else setEditForm({ ...editForm, payment_type: v });
              }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Payment</SelectItem>
                  <SelectItem value="partial">Partial Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editForm.payment_type === 'partial' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold">Paid Amount</label>
                  <Input type="number" step="0.01" value={editForm.paid_amount || ''} onChange={(e) => {
                    const paid = parseFloat(e.target.value) || 0;
                    const rem = (editForm.amount || 0) - paid;
                    setEditForm({ ...editForm, paid_amount: paid, remaining_balance: Math.max(rem, 0), payment_status: rem <= 0 ? 'paid' : 'pending' });
                  }} />
                </div>
                <div>
                  <label className="text-sm font-semibold">Remaining</label>
                  <Input type="number" value={editForm.remaining_balance || 0} readOnly className="bg-gray-50" />
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button onClick={handleUpdate} className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white"><Check size={16} className="mr-2" /> Save</Button>
              <Button onClick={() => setEditingExpense(null)} variant="outline" className="flex-1"><X size={16} className="mr-2" /> Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesDetailView;
